/** Tempering (opened from the smith): raise gear +N with success chance, Forge Resolve pity and materials. */
import { useEffect, useState } from 'preact/hooks';
import { NPCS } from '../../content/npcs';
import { countOf } from '../../game/inventory';
import { GEAR_SLOTS, type GearItem, ITEMS, gearIlvl } from '../../game/items';
import { MAX_CATALYSTS, RESOLVE_GAIN, temperChance } from '../../game/tempering';
import { ui } from '../../ui/app/store';
import { Window } from '../../ui/app/Window';
import { itemIcon } from './art';
import { type Ctx, ItemIcon, Money, commit, notify, useCtx } from './common';
import { keyTrap } from './keys';
import { SLOT_NAME, type TemperRun, fmt, fmtPct, temperLadder, temperRepeat } from './logic';
import { ItemTooltip, tip } from './Tooltip';

interface Result {
  n: number;
  run: TemperRun;
  name: string;
  toPlus: number;
  toIlvl: number;
  resolve: number;
}

function GearRow({ g, sel, onPick, weapon, equipped }: { g: GearItem; sel: boolean; onPick: () => void; weapon?: string; equipped: boolean }) {
  const maxed = g.plus >= g.maxPlus;
  return (
    <button type="button" class={`btp-row ${sel ? 'is-sel' : ''} ${maxed ? 'is-max' : ''}`} role="option" aria-selected={sel} onClick={onPick} {...tip(() => <ItemTooltip item={g} where={equipped ? 'equipped' : 'bag'} />)}>
      <ItemIcon item={g} size={40} weapon={weapon} />
      <span class="btp-row-t">
        <span class={`btp-row-n r-${g.rarity}`}>
          {g.plus > 0 && <b>+{g.plus} </b>}
          {g.name}
        </span>
        <span class="btp-row-m">
          {SLOT_NAME[g.slot]} · iLvl {fmt(gearIlvl(g))}
        </span>
      </span>
      {maxed ? <span class="btp-chip">MAX</span> : g.resolve > 0 ? <span class="btp-chip btp-chip--res">{Math.round(g.resolve * 100)}%</span> : null}
    </button>
  );
}

function ResultView({ r, onClose }: { r: Result; onClose: () => void }) {
  const ok = r.run.success;
  const tries = r.run.attempts.length;
  return (
    <button type="button" class={`btp-result ${ok ? 'is-ok' : 'is-fail'}`} onClick={onClose} aria-live="assertive">
      {ok && <span class="btp-burst" aria-hidden="true" />}
      <span class="btp-result-t">{ok ? 'Tempering Success' : 'Tempering Failed'}</span>
      {ok ? (
        <>
          <span class="btp-result-stage">
            +{r.run.fromPlus} <i>»»»</i> +{r.toPlus}
          </span>
          <span class="btp-result-ilvl">
            Item Level {fmt(r.run.fromIlvl)} <i>»</i> <b>{fmt(r.toIlvl)}</b> <em>▲ {fmt(r.toIlvl - r.run.fromIlvl)}</em>
          </span>
          {tries > 1 && <span class="btp-result-sub">Succeeded on attempt {tries}</span>}
        </>
      ) : (
        <>
          <span class="btp-result-sub">
            Forge Resolve {fmtPct(r.run.fromResolve)} <i>»</i> <b>{fmtPct(r.resolve)}</b>
          </span>
          <span class="btp-result-sub">
            {tries > 1 ? `${tries} attempts failed` : 'The metal resists — your resolve grows.'}
            {r.run.reason === 'unaffordable' && tries > 0 ? ' · out of materials' : ''}
          </span>
        </>
      )}
    </button>
  );
}

function Socket({ id, need, have }: { id: string; need: number; have: number }) {
  const def = ITEMS[id];
  const ok = have >= need;
  return (
    <div class={`btp-sock ${ok ? 'is-ok' : 'is-short'}`} {...tip(() => <ItemTooltip item={id} />)}>
      <span class={`btp-sock-ring rbg-${def?.rarity ?? 'normal'}`}>
        <img src={itemIcon(id)} alt="" />
      </span>
      <span class="btp-sock-n">
        <b>{fmt(have)}</b>/{fmt(need)}
      </span>
      <span class="btp-sock-l">{def?.name ?? id}</span>
    </div>
  );
}

function Forge({ ctx, g, result, setResult }: { ctx: Ctx; g: GearItem; result: Result | null; setResult: (r: Result | null) => void }) {
  const { p, c, a, cls } = ctx;
  const [cats, setCats] = useState(0);
  const [tome, setTome] = useState(false);
  const pv = p.temperPreview(g);
  const step = pv.step;
  const cost = pv.cost;
  const catOwned = countOf(c, 'catalyst');
  const tomeOwned = countOf(c, 'forge_tome');
  const catN = Math.min(cats, catOwned, MAX_CATALYSTS);
  const tomeOk = !!step && step.plus >= 11 && tomeOwned > 0;
  const useTome = tome && tomeOk;
  const guaranteed = g.resolve >= 1;
  // Break the chance down using the game's own rule.
  const clean = { ...g, fails: 0, resolve: 0 };
  const base = temperChance(clean, 0, useTome);
  const withFails = temperChance({ ...g, resolve: 0 }, 0, useTome);
  const withCats = temperChance({ ...g, resolve: 0 }, catN, useTome);
  const chance = temperChance(g, catN, useTome);
  const failBonus = withFails - base;
  const catBonus = withCats - withFails;
  const gain = Math.min(1 - g.resolve, chance * RESOLVE_GAIN);
  const next = temperLadder(g)[g.plus + 1];
  const weapon = g.slot === 'weapon';

  const run = (times: number) => {
    const out = temperRepeat(p, g, times, catN, useTome);
    if (!out.attempts.length) {
      notify(out.reason === 'maxed' ? 'This item is fully tempered.' : 'Not enough materials or currency.', '#e5484d');
      return;
    }
    setResult({ n: Date.now(), run: out, name: g.name, toPlus: g.plus, toIlvl: gearIlvl(g), resolve: g.resolve });
    const left = countOf(c, 'catalyst');
    if (cats > left) setCats(left);
    if (tome && countOf(c, 'forge_tome') === 0) setTome(false);
    commit();
  };

  if (!step || !cost) {
    return (
      <div class="btp-forge">
        <div class="btp-gate is-max">
          <span class="btp-ring" />
          <span class="btp-orb">
            <img src={itemIcon(g, cls?.weapon)} alt="" />
          </span>
        </div>
        <div class="btp-stage">
          <span class={`r-${g.rarity}`}>{g.name}</span>
        </div>
        <p class="btp-maxed">Fully tempered at +{g.plus}. Nothing more can be drawn from this metal.</p>
        {result && <ResultView r={result} onClose={() => setResult(null)} />}
      </div>
    );
  }

  const mats: { id: string; need: number }[] = [
    { id: 'aether_shard', need: cost.shards },
    { id: weapon ? 'ore_weapon' : 'ore_armor', need: cost.ore },
    { id: 'leap_crystal', need: cost.leap },
    ...(cost.fusion > 0 ? [{ id: 'fusion_alloy', need: cost.fusion }] : []),
  ].filter((m) => m.need > 0);

  const R = 64;
  const circ = 2 * Math.PI * R;
  return (
    <div class="btp-forge">
      <div class="btp-stage">
        <span class={`btp-stage-name r-${g.rarity}`}>{g.name}</span>
        <span class="btp-stage-plus">
          +{g.plus} <i>»»»</i> <b>+{step.plus}</b>
        </span>
        <span class="btp-stage-ilvl">
          Item Level {fmt(gearIlvl(g))} <i>»</i> <b>{fmt(next?.ilvl ?? gearIlvl(g))}</b>
        </span>
      </div>
      <div class={`btp-gate ${guaranteed ? 'is-sure' : ''}`}>
        <span class="btp-ring" />
        <span class="btp-ring btp-ring--2" />
        <svg class="btp-arc" viewBox="0 0 160 160" aria-hidden="true">
          <circle cx="80" cy="80" r={R} class="btp-arc-track" />
          {g.resolve > 0 && <circle cx="80" cy="80" r={R} class="btp-arc-fill" style={{ strokeDasharray: `${circ * Math.min(1, g.resolve)} ${circ}` }} />}
        </svg>
        <span class="btp-orb" {...tip(() => <ItemTooltip item={g} />)}>
          <img src={itemIcon(g, cls?.weapon)} alt="" />
        </span>
      </div>
      <div class="btp-chance">
        <span>Success chance</span>
        <b class={guaranteed ? 'is-sure' : ''}>{guaranteed ? '100%' : fmtPct(chance, chance < 0.1 ? 1 : 0)}</b>
        <span class="btp-chance-parts">
          Base {fmtPct(base, 0)}
          {failBonus > 0.0005 && (
            <>
              {' '}
              · <em>+{fmtPct(failBonus)}</em> from {g.fails} failure{g.fails === 1 ? '' : 's'}
            </>
          )}
          {catBonus > 0.0005 && (
            <>
              {' '}
              · <em>+{fmtPct(catBonus)}</em> catalysts
            </>
          )}
        </span>
      </div>
      <div class="btp-resolve" {...tip(() => <div class="btt-note">Forge Resolve fills with every failure. At 100% your next attempt is guaranteed to succeed.</div>)}>
        <span class="btp-resolve-l">Forge Resolve</span>
        <span class="btp-resolve-bar">
          <i style={{ transform: `scaleX(${Math.min(1, g.resolve)})` }} />
        </span>
        <b>{fmtPct(g.resolve)}</b>
        <small>{guaranteed ? 'Full resolve: the next attempt is guaranteed.' : chance >= 1 ? 'This attempt cannot fail.' : `A failure adds +${fmtPct(gain)} resolve and +10% of the base chance (up to +100%).`}</small>
      </div>
      <div class="btp-mats">
        {mats.map((m) => (
          <Socket key={m.id} id={m.id} need={m.need} have={countOf(c, m.id)} />
        ))}
      </div>
      <div class="btp-opts">
        <div class="btp-cat" {...tip(() => <ItemTooltip item="catalyst" />)}>
          <img src={itemIcon('catalyst')} alt="" />
          <span class="btp-opt-l">
            Catalysts <small>{fmt(catOwned)} owned</small>
          </span>
          <span class="btp-step" role="group" aria-label="Catalysts">
            <button type="button" class="btp-step-b" disabled={catN <= 0} onClick={() => setCats(Math.max(0, catN - 1))} aria-label="Fewer catalysts">
              −
            </button>
            <b>{catN}</b>
            <button type="button" class="btp-step-b" disabled={catN >= Math.min(MAX_CATALYSTS, catOwned)} onClick={() => setCats(Math.min(MAX_CATALYSTS, catOwned, catN + 1))} aria-label="More catalysts">
              +
            </button>
            <button type="button" class="btp-step-max" disabled={catN >= Math.min(MAX_CATALYSTS, catOwned)} onClick={() => setCats(Math.min(MAX_CATALYSTS, catOwned))}>
              Max
            </button>
          </span>
        </div>
        <label class={`btp-tome ${tomeOk ? '' : 'is-off'}`} {...tip(() => <ItemTooltip item="forge_tome" />)}>
          <input type="checkbox" checked={useTome} disabled={!tomeOk} onChange={() => setTome(!tome)} />
          <span class="btp-check" />
          <img src={itemIcon('forge_tome')} alt="" />
          <span class="btp-opt-l">
            Forgemaster Tome <small>{step.plus >= 11 ? `${fmt(tomeOwned)} owned · doubles base chance` : 'for +11 to +15'}</small>
          </span>
        </label>
      </div>
      <div class="btp-cost">
        <span>Cost</span>
        <span class={a.wallet.silver >= cost.silver ? '' : 'is-short'}>
          <Money silver={cost.silver} />
        </span>
        {cost.gold > 0 && (
          <span class={a.wallet.gold >= cost.gold ? '' : 'is-short'}>
            <Money gold={cost.gold} />
          </span>
        )}
        <span class="btp-cost-have">
          Have <Money silver={a.wallet.silver} gold={a.wallet.gold} />
        </span>
      </div>
      <div class="btp-go">
        <button type="button" class="btn btn--gold btp-go-main" disabled={!pv.affordable} onClick={() => run(1)}>
          Temper
        </button>
        <button type="button" class="btn btp-go-10" disabled={!pv.affordable} onClick={() => run(10)} {...tip(() => <div class="btt-note">Up to ten attempts, stopping at the first success or when materials run out.</div>)}>
          Temper ×10
        </button>
      </div>
      {result && <ResultView r={result} onClose={() => setResult(null)} />}
    </div>
  );
}

export function TemperPanel() {
  const ctx = useCtx();
  const [uid, setUid] = useState<string | null>(null);
  const [result, setResult] = useState<Result | null>(null);
  useEffect(() => {
    if (!result) return;
    const t = setTimeout(() => setResult(null), result.run.success ? 3200 : 2400);
    return () => clearTimeout(t);
  }, [result]);
  if (!ctx) return null;
  const { c, cls } = ctx;
  const npc = NPCS[ui.panelArg.value ?? ''];
  const equipped = GEAR_SLOTS.map((s) => c.equipment[s]).filter((x): x is GearItem => !!x);
  const bag = c.inventory.items.filter((i): i is GearItem => i.kind === 'gear');
  const all = [...equipped, ...bag];
  const g = all.find((x) => x.uid === uid) ?? equipped.find((x) => x.plus < x.maxPlus) ?? all[0];
  const pick = (id: string) => {
    setUid(id);
    setResult(null);
  };
  return (
    <Window title="Tempering" width={1060} height={700} className="bwin bwin--temper">
      <div class="btp" {...keyTrap}>
        <div class="btp-list" role="listbox" aria-label="Gear">
          {npc && (
            <div class="btp-npc">
              {npc.name} <span>· {npc.role}</span>
            </div>
          )}
          <div class="btp-list-h section-h">Equipped</div>
          {equipped.length === 0 && <p class="btp-none">Nothing equipped.</p>}
          {equipped.map((x) => (
            <GearRow key={x.uid} g={x} sel={x.uid === g?.uid} equipped weapon={cls?.weapon} onPick={() => pick(x.uid)} />
          ))}
          <div class="btp-list-h section-h">In your bag</div>
          {bag.length === 0 && <p class="btp-none">No gear in your bag.</p>}
          {bag.map((x) => (
            <GearRow key={x.uid} g={x} sel={x.uid === g?.uid} equipped={false} weapon={cls?.weapon} onPick={() => pick(x.uid)} />
          ))}
        </div>
        {g ? (
          <Forge key={g.uid} ctx={ctx} g={g} result={result} setResult={setResult} />
        ) : (
          <div class="btp-forge btp-forge--empty">
            <p>Bring me a weapon or armour and I will temper it.</p>
          </div>
        )}
        <div class="btp-ladder">
          <div class="section-h">Stages</div>
          {g && (
            <ol class="btp-steps">
              {temperLadder(g)
                .slice()
                .reverse()
                .map((s) => (
                  <li key={s.plus} class={s.plus === g.plus ? 'is-cur' : s.plus === g.plus + 1 ? 'is-next' : s.plus < g.plus ? 'is-done' : ''}>
                    <span>+{s.plus}</span>
                    <b>{fmt(s.ilvl)}</b>
                  </li>
                ))}
            </ol>
          )}
        </div>
      </div>
    </Window>
  );
}
